import React, { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Popover, PopoverBody } from 'reactstrap'
import uniqueId from 'lodash/uniqueId'
import { tenis10CategoryLabel } from '../../../tenis10Event'

export function competitionIdentity(c) {
  const r = c.restrictions
  const level = Number.isFinite(c.level) && c.level > 0 ? c.level : null
  // A Tenis10 competition is named by its Tenis10 category, „Verde · U10”, not by the adult level the schema gives it (PRD 16 LR11).
  const levelLabel = c.tenis10Level ? tenis10CategoryLabel(c) : 'Level ' + (level ?? '—')
  const gameLabel = c.gameType ? c.gameType.charAt(0).toUpperCase() + c.gameType.slice(1) : 'Unavailable'
  const markers = []
  const rules = []
  if (r?.teamAverageLevel) {
    if (c.gameType === 'doubles' && level !== null) markers.push({ key: 'average', label: `(${level - 0.5})`, help: `Maximum team average: ${level - 0.5}. The two players’ doubles levels must average ${level - 0.5} or lower.` })
    else rules.push({ key: 'average', label: 'Team average: unavailable', help: 'The team average restriction is set, but its level cannot be determined.' })
  }
  const gender = { female: ['W', 'Women only.'], male: ['M', 'Men only.'], mixt: ['X', 'Mixed doubles: one woman and one man.'] }[r?.gender]
  if (gender) markers.push({ key: 'gender', label: `(${gender[0]})`, help: gender[1] })
  else if (r?.gender) rules.push({ key: 'gender', label: r.gender, help: 'Recorded gender restriction: ' + r.gender })
  if (r?.forbiddenToTeams) rules.push({ key: 'cap', icon: 'user', label: level === null ? 'Max level / player: unavailable' : `Max L${level} / player`, help: level === null ? 'The individual doubles cap is set, but its level is unavailable.' : `Each player must have a doubles level of ${level} or lower. A higher-level partner cannot enter, even if the team average meets the limit.` })
  if (r?.minAge || r?.maxAge) {
    const age = r.minAge && r.maxAge ? `${r.minAge}–${r.maxAge}` : r.minAge ? `${r.minAge}+` : `≤${r.maxAge}`
    rules.push({ key: 'age', icon: 'calendar', label: `Age: ${age}`, help: `Age ${age}, inclusive. Age is the current calendar year minus the birth year.` })
  }
  if (r?.rank > 0) rules.push({ key: 'rank', icon: 'trending-up', label: `Rank: ${r.rank}+`, help: `Ranking position ${r.rank} or lower (a larger position number).` })
  if (r?.accountType === 'summerAndPremium') rules.push({ key: 'membership', icon: 'star', label: 'Premium', help: 'Requires an eligible Premium membership, including the supported Summer plan.' })
  if (r?.accountActivity && r.accountActivity !== 'any') {
    const parts = r.accountActivity.split('-')
    const minimum = Number(parts[0])
    const recent = parts.includes('52weeks')
    rules.push(Number.isFinite(minimum) && minimum > 0
      ? { key: 'activity', icon: 'pulse', label: `${minimum}+ comps / ${recent ? '52w' : 'all time'}`, help: `At least ${minimum} distinct competitions recorded in player activity${recent ? ' during the last 52 weeks' : ' across the full history'}. This is not a match count.` }
      : { key: 'activity', icon: 'pulse', label: 'Activity: unavailable', help: 'An activity restriction is set, but its minimum cannot be determined.' })
  }
  return { levelLabel, gameLabel, markers, rules, name: [levelLabel, ...markers.map(m => m.label)].join(' ') + ' · ' + gameLabel }
}

function RestrictionHint({ rule, marker = false }) {
  const target = useRef(null)
  const [open, setOpen] = useState(false)
  const [helpId] = useState(() => uniqueId('competition-restriction-help-'))
  const toggle = event => setOpen(value => event?.type === 'click' && target.current?.contains(event.target) ? true : !value)
  return <>
    <button ref={target} type="button" className={marker ? 'competition-title-marker' : 'competition-restriction-chip'} aria-label={rule.label} aria-describedby={open ? helpId : undefined}>
      {!marker && rule.icon && <i className={'bx bx-' + rule.icon} aria-hidden="true" />}{rule.label}{!marker && <i className="bx bx-info-circle" aria-hidden="true" />}
    </button>
    <Popover id={helpId} target={target} isOpen={open} toggle={toggle} trigger="hover focus legacy" placement="bottom" fade={false}><PopoverBody>{rule.help}</PopoverBody></Popover>
  </>
}

export function CompetitionTitle({ competition, to }) {
  const { name, levelLabel, gameLabel, markers } = competitionIdentity(competition)
  if (!markers.length) return <Link to={to}>{name}</Link>
  return <><Link to={to} aria-label={name}>{levelLabel}</Link>{markers.map(rule => <React.Fragment key={rule.key}> <RestrictionHint rule={rule} marker /></React.Fragment>)} · {gameLabel}</>
}

export function CompetitionRestrictionBoxes({ competition }) {
  if (!competition.restrictions) return <small>Restrictions unavailable</small>
  const { rules } = competitionIdentity(competition)
  if (!rules.length) return null
  return <div className="competition-restrictions" role="group" aria-label="Restrictions"><span className="competition-restrictions-label">Restrictions:</span><div className="competition-restriction-chips">{rules.map(rule => <RestrictionHint key={rule.key} rule={rule} />)}</div></div>
}
